import { Box, useTheme } from "@mui/material";

export default function StepDotList({
    labels,
    currentStep,
    maxReachedStep,
    onJumpTo,
    orientation = "vertical",
}) {
    const theme = useTheme();
    const w = theme.custom?.wizard ?? {};
    const isHorizontal = orientation === "horizontal";
    const easing = w.motion?.easing ?? "cubic-bezier(0.22,1,0.36,1)";
    const amber = w.amber ?? "#E8893B";

    return (
        <Box
            component="ol"
            role="list"
            sx={{
                listStyle: "none", m: 0, p: 0,
                display: "flex",
                flexDirection: isHorizontal ? "row" : "column",
                gap: isHorizontal ? 0 : 2,
                justifyContent: isHorizontal ? "space-between" : "flex-start",
                position: "relative",
                alignItems: isHorizontal ? "center" : "flex-start",
                width: isHorizontal ? "100%" : "auto",
                maxWidth: isHorizontal ? 360 : "none",
                mx: isHorizontal ? "auto" : 0,
            }}
        >
            {/* Connector rail behind the dots — runs through the dot centres.
                Horizontal centres sit 22px (half a 44px tap target) in from each
                edge; vertical rail matches the 20px dot's centre at left 9.5. */}
            <Box
                aria-hidden
                sx={isHorizontal ? {
                    position: "absolute",
                    left: 22, right: 22,
                    top: "50%",
                    height: "1px",
                    transform: "translateY(-0.5px)",
                    background: `linear-gradient(to right, ${theme.palette.divider}80 0%, ${theme.palette.divider}30 100%)`,
                    zIndex: 0,
                } : {
                    position: "absolute",
                    left: 9.5,
                    top: 14,
                    bottom: 14,
                    width: "1px",
                    background: `linear-gradient(to bottom, ${theme.palette.divider}80 0%, ${theme.palette.divider}10 100%)`,
                    zIndex: 0,
                }}
            />
            {labels.map((label, idx) => {
                // State is driven by maxReachedStep, not just position: a step the
                // user has already reached but is now *ahead* of (after jumping back)
                // must still read as done/reachable, not as an untouched future step.
                const isCurrent = idx === currentStep;
                const isFuture = idx > maxReachedStep;
                const isDone = !isCurrent && !isFuture; // behind us OR reached-then-left
                const clickable = isDone;
                const scale = isCurrent ? 1 : 0.8;
                const fill = isFuture ? "transparent" : amber;
                const border = isFuture ? `${theme.palette.divider}80` : "transparent";
                // Clickable steps are real buttons (keyboard + screen-reader
                // actionable); the current/unreached markers are inert divs.
                const Tag = clickable ? "button" : "div";
                return (
                    <Box
                        component="li"
                        key={label}
                        sx={{ position: "relative", zIndex: 1, display: "flex" }}
                    >
                        <Box
                            component={Tag}
                            type={clickable ? "button" : undefined}
                            onClick={clickable ? () => onJumpTo(idx) : undefined}
                            aria-current={isCurrent ? "step" : undefined}
                            // Horizontal dots carry no visible label, so name them for SRs;
                            // vertical rows render the label text inline (no aria-label needed).
                            aria-label={isHorizontal ? (clickable ? `Siirry vaiheeseen: ${label}` : label) : undefined}
                            sx={{
                                appearance: "none",
                                background: "none",
                                border: 0,
                                font: "inherit",
                                color: "inherit",
                                textAlign: "left",
                                p: 0,
                                m: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: isHorizontal ? "center" : "flex-start",
                                gap: isHorizontal ? 0 : 1.25,
                                // ≥44px touch target on mobile (horizontal); compact rows
                                // on desktop stay tight but keep a comfortable hit height.
                                minWidth: isHorizontal ? 44 : 0,
                                minHeight: isHorizontal ? 44 : 28,
                                py: isHorizontal ? 0 : 0.25,
                                cursor: clickable ? "pointer" : "default",
                                outline: "none",
                                borderRadius: `${w.fieldRadius ?? 10}px`,
                                "&:focus-visible": {
                                    outline: `2px solid ${amber}`,
                                    outlineOffset: 2,
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    width: 20,
                                    height: 20,
                                    flexShrink: 0,
                                    borderRadius: "50%",
                                    backgroundColor: fill,
                                    border: `1.5px solid ${border}`,
                                    // Scale the dot rather than animate width/height — a
                                    // transform doesn't trigger layout, and a fixed 20px box
                                    // keeps every dot centered on the rail.
                                    transform: `scale(${scale})`,
                                    boxShadow: isCurrent
                                        ? `0 0 0 4px rgba(232,137,59,0.18), 0 0 16px rgba(232,137,59,0.35)`
                                        : "none",
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    transition: `transform 160ms ${easing}, box-shadow 220ms ${easing}, background-color 220ms ${easing}`,
                                    "@media (prefers-reduced-motion: reduce)": {
                                        transition: "none",
                                    },
                                }}
                            >
                                {isDone && (
                                    <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden>
                                        <polyline points="2,5 4,7 8,3" fill="none" stroke="#1A1A1A" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                )}
                            </Box>
                            {!isHorizontal && (
                                <Box sx={{
                                    fontFamily: "'Inter', sans-serif",
                                    fontSize: 11,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.10em",
                                    color: isCurrent ? "text.primary" : "text.secondary",
                                    fontWeight: isCurrent ? 600 : 400,
                                    userSelect: "none",
                                }}>
                                    {label}
                                </Box>
                            )}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
}
